import { Injectable } from '@angular/core';

/**
 * ElectronBridgeService
 * Thin wrapper around window.electronAPI and window.host IPC channels
 * Provides type-safe access to Electron renderer→main communication
 */
@Injectable({ providedIn: 'root' })
export class ElectronBridgeService {
  private get api() {
    return (window as any).electronAPI;
  }

  get host() {
    return (window as any).host;
  }

  private get windowHost() {
    return (window as any).windowHost;
  }

  /**
   * Check if running in Electron environment
   */
  get isElectron(): boolean {
    return !!(window as any).electronAPI && !!(window as any).host;
  }

  // ============================================================================
  // Browser Navigation & Control
  // ============================================================================

  /**
   * Navigate to a URL in the BrowserView
   */
  async navigate(url: string): Promise<void> {
    if (!this.isElectron) return;
    return this.host.navigate(url);
  }

  /**
   * Navigate back in the BrowserView
   */
  async back(): Promise<void> {
    if (!this.isElectron) return;
    return this.host.back();
  }

  /**
   * Navigate forward in the BrowserView
   */
  async forward(): Promise<void> {
    if (!this.isElectron) return;
    return this.host.forward();
  }

  /**
   * Reload the current page in the BrowserView
   */
  async reload(): Promise<void> {
    if (!this.isElectron) return;
    return this.host.reload();
  }

  /**
   * Get the current URL from the BrowserView
   */
  async getUrl(): Promise<string> {
    if (!this.isElectron) return '';
    return this.host.getUrl();
  }

  // ============================================================================
  // Test Execution APIs
  // ============================================================================

  /**
   * Execute a single test step on the current page
   * @param step Step definition (selector, action, value, etc.)
   */
  async executeStep(step: any): Promise<any> {
    if (!this.isElectron) return null;
    return this.host.executeStep(step);
  }

  /**
   * Get current page state (DOM elements, text, attributes)
   */
  async getPageState(): Promise<any> {
    if (!this.isElectron) return null;
    return this.host.getPageState();
  }

  /**
   * Get element selector map for all interactive elements on page
   */
  async getElementMap(): Promise<any> {
    if (!this.isElectron) return null;
    return this.host.getElementMap();
  }

  /**
   * Get all text nodes from current page
   */
  async getTextElements(): Promise<any> {
    if (!this.isElectron) return null;
    return this.host.getTextElements();
  }

  // ============================================================================
  // Session Management (for gap analysis, preconditions)
  // ============================================================================

  /**
   * Create a hidden BrowserWindow/session for test execution
   * @param opts Options (webPreferences, etc.)
   */
  async createSessionWindow(opts?: any): Promise<string> {
    if (!this.isElectron) return '';
    const result = await this.host.createSessionWindow(opts);
    if (result && typeof result === 'object' && result.handle !== undefined) {
      return result.handle;
    }
    return result;
  }

  /**
   * Destroy a hidden session window
   * @param handle Session handle returned by createSessionWindow
   */
  async destroySessionWindow(handle: string): Promise<void> {
    if (!this.isElectron) return;
    return this.host.destroySessionWindow(handle);
  }

  /**
   * Validate that a session window is still alive
   * @param handle Session handle
   */
  async validateSessionWindow(handle: string): Promise<boolean> {
    if (!this.isElectron) return false;
    return this.host.validateSessionWindow(handle);
  }

  /**
   * Navigate session to URL
   * @param handle Session handle
   * @param url URL to navigate to
   */
  async navigateSession(handle: string, url: string): Promise<void> {
    if (!this.isElectron) return;
    return this.host.navigateSession(handle, url);
  }

  /**
   * Execute a test step in a specific session
   * @param handle Session handle
   * @param step Step definition
   */
  async executeStepInSession(handle: string, step: any): Promise<any> {
    if (!this.isElectron) return null;
    return this.host.executeStepInSession(handle, step);
  }

  /**
   * Evaluate screen detection rules in a session
   * @param handle Session handle
   * @param rules Screen detection rules
   * @param minMatch Minimum match threshold
   */
  async evaluateScreenRules(handle: string, rules: any[], minMatch: number): Promise<boolean> {
    if (!this.isElectron) return false;
    return this.host.evaluateScreenRules(handle, rules, minMatch);
  }

  // ============================================================================
  // Screenshot Capture
  // ============================================================================

  /**
   * Capture screenshot of current page in BrowserView
   */
  async captureScreenshot(): Promise<string> {
    if (!this.isElectron) return '';
    return this.host.captureScreenshot();
  }

  // ============================================================================
  // Session Pool Management (hidden BrowserWindow)
  // ============================================================================

  /**
   * Create a hidden BrowserWindow via windowHost
   * Used by test executor for concurrent session management
   */
  async createHiddenHost(opts?: any): Promise<string> {
    if (!this.isElectron) return '';
    return this.windowHost.createHiddenHost(opts);
  }

  /**
   * Destroy the hidden BrowserWindow
   */
  async destroyHiddenHost(): Promise<void> {
    if (!this.isElectron) return;
    return this.windowHost.destroyHiddenHost();
  }

  // ============================================================================
  // App Metadata
  // ============================================================================

  /**
   * Get app version
   */
  async getAppVersion(): Promise<string> {
    if (!this.isElectron) return '';
    return this.api.getAppVersion();
  }

  /**
   * Get app environment (dev, prod, test)
   */
  async getAppEnv(): Promise<string> {
    if (!this.isElectron) return 'web';
    return this.api.getAppEnv();
  }

  /**
   * Get app configuration
   */
  async getAppConfig(): Promise<any> {
    if (!this.isElectron) return null;
    return this.api.getAppConfig();
  }

  /**
   * Update app configuration (partial)
   */
  async setAppConfig(patch: any): Promise<void> {
    if (!this.isElectron) return;
    return this.api.setAppConfig(patch);
  }
}
